import { optimizeCss } from '@wix/css-optimization'
import type {
	BeckyCssModuleResponse,
	BeckyFunctionLibrary,
	BeckyModel,
	BeckyRequestPayload,
	SiteAssetsModule,
	CssInstanceModel,
	OoiCssInstanceModel,
} from '@wix/thunderbolt-becky-root'
import { createFunctionLibrary, deStringifyExperiments } from '@wix/thunderbolt-becky-root'
import { getViewerCatharsisCss } from '@wix/thunderbolt-catharsis-deployable'
import { getAsyncStore } from '@wix/thunderbolt-async-state-manager-store'
import type { AsyncStores, AsyncNodes } from '@wix/thunderbolt-async-state-manager-store'
import { resolveBeckyModel } from './helpers/resolveBeckyModel'
import { getStylableInstance, GetStylableInstanceParams } from './stylable'
import byRefModule from '../byRef'
import { BeckyExperiments } from '@wix/thunderbolt-commons'
import { BeckyTopology, FetchFn, MetricsReporter, ModuleFetcher, Logger } from '@wix/thunderbolt-becky-types'
import { maybeAppendErrorPages } from '../errorPages/getPayloadWithErrorPage'
import { BeckyFetch } from './utils/beckyFetch'
import { getBeckyTopology } from './helpers/topologyBuilder'
import { loadCssConfigs, processOoiCssImpl } from './helpers/ooiCssProcessor'
import LRU from 'lru-cache'
import { cssModelFactory, ooiCssModelFactory } from '@wix/thunderbolt-becky-carmi-ds/dist/carmiEntries/css-entry'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'
import { resolveWidgetsWithAppSettings } from './styleParamsOverrides/resolveAppSettings'
import { getViewerStateManagerCss } from '@wix/thunderbolt-css'
import { clearGlobalThis, setGlobalThis } from './globalThisUtils'
import { BeckyModelCssModuleResponse } from '@wix/thunderbolt-becky-root/dist/types/becky'
import { addInvalidation as addShapesInvalidation, fetchShapes } from '@wix/thunderbolt-fetch-shapes'
import { addInvalidation as addPresetsInvalidation, fetchPresets } from '@wix/thunderbolt-fetch-presets'
import { addInvalidation as addManifestInvalidation, fetchManifests } from '@wix/thunderbolt-fetch-manifests'

const shouldSplitByPresets = (payload: BeckyRequestPayload) => payload.shouldSplitByPresets === 'true'
const shouldRunVsmFromPayload = (payload: BeckyRequestPayload) => payload.shouldRunVsm === 'true'
const shouldReturnResolvedBeckyModelFromPayload = (payload: BeckyRequestPayload) =>
	payload.shouldReturnResolvedBeckyModel === 'true'

const shouldReturnCssObject = (payload: BeckyRequestPayload) => payload.shouldReturnCssObject === 'true'

export const getCssResult = async ({
	payload,
	beckyTopology,
	funcLib,
	moduleFetcher,
	fetchFn,
	metricsReporter,
	logger,
}: {
	payload: BeckyRequestPayload
	beckyTopology: BeckyTopology
	funcLib: BeckyFunctionLibrary
	moduleFetcher: ModuleFetcher
	fetchFn: FetchFn
	metricsReporter: MetricsReporter
	logger: Logger
}): Promise<CssInstanceModel> => {
	const beckyModel = await funcLib.runAsyncAndReport(
		() => resolveBeckyModel({ payload, beckyTopology, fetchFn, metricsReporter, logger }),
		'resolveBeckyModel'
	)

	setGlobalThis(beckyModel)

	const asyncNodes: AsyncNodes = [
		{
			addInvalidation: addShapesInvalidation,
			afterInvalidation: (shapes) => {
				return funcLib.runAsyncAndReport(() => {
					return fetchShapes(
						shapes,
						fetchFn,
						beckyTopology.environmentTopology.mediaRootUrl,
						funcLib.buildSvgUrl,
						metricsReporter
					)
				}, 'tb-css-fetch-shapes')
			},
			name: 'shapeStore',
		},
		{
			addInvalidation: addManifestInvalidation,
			afterInvalidation: (widgets) => {
				return funcLib.runAsyncAndReport(() => {
					const { blocksBuilderManifestGeneratorVersion, dfVersion } = payload
					return fetchManifests(
						widgets,
						fetchFn,
						beckyModel.clientSpecMap,
						metricsReporter,
						blocksBuilderManifestGeneratorVersion,
						dfVersion
					)
				}, 'tb-css-fetch-manifest')
			},
			name: 'manifestStore',
		},
	]

	if (beckyModel.experiments['specs.thunderbolt.BuilderComponent'] && !payload.shouldSplitByPresets) {
		asyncNodes.push({
			addInvalidation: addPresetsInvalidation,
			afterInvalidation: (presets) => {
				return funcLib.runAsyncAndReport(
					() => fetchPresets(presets, fetchFn, metricsReporter, payload, beckyModel),
					'tb-builder-presets-css-fetch'
				)
			},
			name: 'builderPresetsStore',
		})
	}

	// Replace setter with a noop function because fetchApi.carmi assumes that the setter exists
	const ccsFuncLib = { ...funcLib, setFetchRequestsStore: () => metricsReporter.reportError }

	const asyncStorePromise = funcLib.runAsyncAndReport(
		async () => getAsyncStore(asyncNodes, beckyModel, metricsReporter, schemas),
		'tb-css-async-stores'
	) as Promise<AsyncStores>

	const ooiCssModelPromise: Promise<OoiCssInstanceModel> = funcLib.runAsyncAndReport(
		async () => Promise.resolve<OoiCssInstanceModel>(ooiCssModelFactory(beckyModel, ccsFuncLib)),
		'ooiCssModelFactory'
	)

	const [asyncStore, ooiCssModel] = await Promise.all([asyncStorePromise, ooiCssModelPromise])

	const ooiCssConfigStore = await loadCssConfigs(
		payload.ooiVersions!,
		ooiCssModel.widegtsIds,
		moduleFetcher,
		funcLib.runAsyncAndReport,
		logger
	)

	const ooiWidgetsAppSettings = await resolveWidgetsWithAppSettings(
		ooiCssModel.widgetsWithReferenceId,
		beckyModel.clientSpecMap,
		metricsReporter
	)

	const isCatharsisOOICss = beckyModel.experiments['specs.thunderbolt.compCssMappers_catharsis']
	const beckyModelWithConfigsAndAsyncStores: BeckyModel = {
		...beckyModel,
		...asyncStore,
		ooiCssConfigStore,
		ooiWidgetsAppSettings,
		featuresToRun: payload.featuresToRun ? payload.featuresToRun.split(',') : [],
		featuresToIgnore: payload.featuresToIgnore ? payload.featuresToIgnore.split(',') : [],
	}

	const processOoiCss = isCatharsisOOICss
		? () => ''
		: await funcLib.runAsyncAndReport(
				() =>
					processOoiCssImpl({
						funcLib,
						payload,
						beckyModel: beckyModelWithConfigsAndAsyncStores,
					}),
				'processOoiCssImpl'
		  )

	const runAndReport = <T>(fn: () => T, name: string) => {
		const dateBefore = Date.now()
		const res = fn()
		const dateAfter = Date.now()
		logger.info(name, { duration: dateAfter - dateBefore })
		return res
	}

	return funcLib.runAndReport(() => {
		const shouldRunVsm = shouldRunVsmFromPayload(payload)
		const cssResponse = funcLib.runAndReport(
			() =>
				shouldRunVsm
					? getViewerStateManagerCss(beckyModelWithConfigsAndAsyncStores, runAndReport)
					: getViewerCatharsisCss(beckyModelWithConfigsAndAsyncStores, schemas, runAndReport),
			shouldRunVsm ? 'get-vsm-css' : 'get-catharsis-css'
		)

		const cssResultObject: { cssResultObject?: typeof cssResponse['css'] } = shouldReturnCssObject(payload)
			? { cssResultObject: cssResponse.css }
			: {}

		if (shouldReturnResolvedBeckyModelFromPayload(payload)) {
			/* This is for debugging CSS locally.
			The way it works would be:
				1. css module returns serialized and resolved beckyModel (thus stylableInstance is not included)
				2. Client side, via PageStyleLoader, will dynamically import VSM and run it with resolved beckyModel
		 	**/
			const beckyResponse = beckyModelWithConfigsAndAsyncStores
			beckyResponse.stylableInstance = undefined
			return { beckyModel: JSON.stringify(beckyResponse) }
		}

		const catharsisCss = cssResponse.cssResult

		if (shouldSplitByPresets(payload)) {
			return { css: catharsisCss, ...cssResultObject }
		}
		const result = funcLib.runAndReport(
			() =>
				cssModelFactory(beckyModelWithConfigsAndAsyncStores, {
					...ccsFuncLib,
					processOoiCss,
				}),
			'get-carmi-css'
		)
		const carmiCss = beckyModelWithConfigsAndAsyncStores.featuresToRun?.length ? '' : result.css

		const css = beckyModelWithConfigsAndAsyncStores.experiments['specs.thunderbolt.compCssMappers_catharsis']
			? carmiCss + catharsisCss
			: catharsisCss + carmiCss

		clearGlobalThis()

		return { ...result, css, ...cssResultObject }
	}, 'tb-css-model-calculation')
}

const getCssResultWithStylableInstance = async (
	payload: BeckyRequestPayload,
	beckyTopology: BeckyTopology,
	experiments: BeckyExperiments,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	funcLib: BeckyFunctionLibrary,
	moduleFetcher: ModuleFetcher,
	logger: Logger
) => {
	const shouldSkipInitStylableInstance =
		(payload.featuresToRun?.length && !payload.featuresToRun?.includes('stylableCss')) ||
		payload.featuresToIgnore?.includes('stylableCss')

	let maybeStylableInstance
	let partialStylableInstanceParams: Partial<GetStylableInstanceParams> = {}
	if (!shouldSkipInitStylableInstance) {
		const getFontsFromExternal = !!experiments['specs.thunderbolt.fontsFromExternal']
		const themeData = await metricsReporter.runAsyncAndReport(async () => {
			return (await payload.pages.masterPage).data.theme_data.THEME_DATA
		}, 'tb-css-get-theme-data')

		const themeFontsSpxResolved = funcLib.resolveThemeFontsSpx(themeData.font)
		partialStylableInstanceParams = {
			payload,
			fileRepoUrl: beckyTopology.environmentTopology.fileRepoUrl,
			themeData,
			themeFontsSpxResolved,
			staticMediaUrl: beckyTopology.publicTopology.staticMediaUrl,
			getFontsFromExternal,
		}
		maybeStylableInstance = await metricsReporter.runAsyncAndReport(async () => {
			return getStylableInstance({
				...partialStylableInstanceParams,
				fetch: fetchFn,
				metricsReporter,
				cache: stylableCache,
			} as GetStylableInstanceParams)
		}, 'tb-css-get-stylable-instance')
	}

	// @ts-ignore
	const { css, beckyModel, cssResultObject = {} } = await getCssResult({
		payload: {
			stylableInstance: maybeStylableInstance,
			...payload,
		},
		beckyTopology,
		funcLib,
		moduleFetcher,
		fetchFn,
		metricsReporter,
		logger,
	})

	if (beckyModel) {
		return { beckyModel, css: '', stylableInstanceParams: JSON.stringify(partialStylableInstanceParams) }
	}

	if (shouldSplitByPresets(payload)) {
		return { css, cssResultObject }
	}

	try {
		logger.info('Before CSS optimization', { cssSize: css.length })

		const optimizedCss = await metricsReporter.runAsyncAndReport(async () => {
			return await optimizeCss(css, {
				variablize: false,
			})
		}, 'tb-css-optimization')

		logger.info('After CSS optimization', {
			cssSize: optimizedCss.length,
		})

		return {
			css: optimizedCss,
			cssResultObject,
		}
	} catch (e) {
		logger.warn('Optimizing CSS failed', e)
	}

	return { css, cssResultObject }
}

const stylableCache = new LRU<string, any>({ max: 20 })

const cssModule: SiteAssetsModule<BeckyCssModuleResponse | BeckyModelCssModuleResponse> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, moduleFetcher, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
		schemas,
	})
	const byRef = byRefModule(moduleContext)

	return {
		execute: async (payload) => {
			const experiments = deStringifyExperiments(payload.beckyExperiments)
			const beckyTopology = getBeckyTopology(topologyGetter, payload)

			return byRef
				.execute(maybeAppendErrorPages(payload))
				.then((payloadWithResolvedRefs) =>
					getCssResultWithStylableInstance(
						payloadWithResolvedRefs,
						beckyTopology,
						experiments,
						fetchFn,
						metricsReporter,
						funcLib,
						moduleFetcher,
						logger
					)
				)
		},
	}
}

export default cssModule
